import React, { useMemo } from "react";
import { aboutData } from "../data/aboutData";
import { contactData } from "../data/contactData";
import { educationData } from "../data/educationData";
import { experienceData, WorkExperience } from "../data/experienceData";
import { heroData } from "../data/heroData";

type ExperienceGroup = {
  company: string;
  period: string;
  roles: WorkExperience[];
};

const FULLTIME_IDS = new Set([
  "rox-group",
  "tokyolife-pm",
  "tokyolife-lead",
  "tokyolife-forlife",
  "tokyolife-cms",
  "fanxipan",
  "ceti",
  "revo",
]);

const FREELANCE_IDS = new Set([
  "moodmade",
  "onspace",
  "tica-live",
  "go-e",
  "nano",
]);

function groupExperiences(items: WorkExperience[]): ExperienceGroup[] {
  const groups: ExperienceGroup[] = [];
  const indexByCompany = new Map<string, number>();

  items.forEach((exp) => {
    const existing = indexByCompany.get(exp.company);
    if (existing === undefined) {
      indexByCompany.set(exp.company, groups.length);
      groups.push({
        company: exp.company,
        period: exp.period,
        roles: [exp],
      });
      return;
    }

    const group = groups[existing];
    group.roles.push(exp);
  });

  return groups;
}

function cleanCompanyName(name: string): string {
  return name.replace(/\s*\(Freelance\)\s*/i, "").replace(/\s*—\s*MVP/i, "");
}

function stripMarkdown(text: string): string {
  return text.replace(/\*\*/g, "");
}

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="mb-2 mt-5 first:mt-0">
    <h2 className="harvard-section-title">{children}</h2>
    <div className="harvard-rule" />
  </div>
);

const ExperienceBlock: React.FC<{ group: ExperienceGroup }> = ({ group }) => {
  const company = cleanCompanyName(group.company);
  const period =
    group.roles.length > 1
      ? group.roles[0].period
      : group.roles[0]?.period ?? group.period;

  return (
    <article className="mb-3.5 break-inside-avoid">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <h3 className="text-[13.5px] font-bold text-neutral-900 tracking-tight">
          {company}
        </h3>
        <span className="text-[12.5px] text-neutral-700 whitespace-nowrap tabular-nums">
          {period}
        </span>
      </div>

      {group.roles.map((role) => (
        <div key={role.id} className="mt-1.5 first:mt-0.5">
          <p className="text-[12.5px] italic text-neutral-800">
            {role.position}
          </p>
          {role.description && (
            <p className="mt-0.5 text-[12px] text-neutral-600 leading-snug">
              {role.description}
            </p>
          )}
          <ul className="mt-1 space-y-0.5 pl-4 list-disc marker:text-neutral-400">
            {role.responsibilities.slice(0, 4).map((item, idx) => (
              <li
                key={idx}
                className="text-[12px] text-neutral-800 leading-[1.45] pl-0.5"
              >
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </article>
  );
};

export const HarvardCV: React.FC = () => {
  const fullTimeGroups = useMemo(
    () =>
      groupExperiences(
        experienceData.experiences.filter((e) => FULLTIME_IDS.has(e.id))
      ),
    []
  );

  const freelanceGroups = useMemo(
    () =>
      groupExperiences(
        experienceData.experiences.filter((e) => FREELANCE_IDS.has(e.id))
      ),
    []
  );

  const education = educationData.education[0];
  const summary = stripMarkdown(aboutData.intro);

  const contactParts = [
    contactData.phone,
    heroData.email,
    contactData.address,
    heroData.socialLinks.github?.replace("https://", ""),
  ].filter(Boolean);

  return (
    <div className="harvard-page min-h-screen bg-neutral-100 text-neutral-900">
      {/* PDF / print actions — hidden when printing */}
      <div className="harvard-toolbar print:hidden sticky top-0 z-40 border-b border-neutral-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-[816px] items-center justify-between gap-3 px-4 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-neutral-900">
              Printable CV (PDF style)
            </p>
            <p className="truncate text-xs text-neutral-500">
              Print or Save as PDF from your browser
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <a
              href={heroData.downloadCvUrl}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-700 hover:bg-neutral-50"
              download
            >
              Download PDF
            </a>
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-md bg-neutral-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-800"
            >
              Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      <div className="harvard-sheet mx-auto my-0 sm:my-8 bg-white shadow-none sm:shadow-sm">
        {/* Header */}
        <header className="text-center pb-1">
          <h1 className="harvard-name">{heroData.name}</h1>
          <p className="mt-1 text-[12.5px] tracking-wide text-neutral-700">
            {heroData.title}
          </p>
          <p className="mt-2 text-[11.5px] text-neutral-600 leading-relaxed flex flex-wrap justify-center gap-x-0">
            {contactParts.map((part, i) => (
              <React.Fragment key={String(part)}>
                {i > 0 && (
                  <span className="mx-1.5 text-neutral-400" aria-hidden>
                    ·
                  </span>
                )}
                <span>{part}</span>
              </React.Fragment>
            ))}
          </p>
        </header>

        {/* Summary */}
        <section>
          <SectionHeading>Professional Summary</SectionHeading>
          <p className="text-[12.5px] text-neutral-800 leading-[1.5] text-justify">
            {summary}
          </p>
        </section>

        {/* Experience */}
        <section>
          <SectionHeading>Experience</SectionHeading>
          {fullTimeGroups.map((group) => (
            <ExperienceBlock key={group.company} group={group} />
          ))}
        </section>

        {/* Freelance / Projects */}
        <section>
          <SectionHeading>Selected Projects & Freelance</SectionHeading>
          {freelanceGroups.map((group) => (
            <ExperienceBlock key={group.company} group={group} />
          ))}
        </section>

        {/* Education */}
        {education && (
          <section>
            <SectionHeading>Education</SectionHeading>
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <h3 className="text-[13.5px] font-bold text-neutral-900">
                {education.institution}
              </h3>
              <span className="text-[12.5px] text-neutral-700 tabular-nums">
                {education.period}
              </span>
            </div>
            <p className="text-[12.5px] italic text-neutral-800 mt-0.5">
              {education.degree}
            </p>
            <ul className="mt-1 space-y-0.5 pl-4 list-disc marker:text-neutral-400">
              <li className="text-[12px] text-neutral-800 leading-[1.45]">
                {education.description}
              </li>
              {education.achievements.map((a) => (
                <li
                  key={a}
                  className="text-[12px] text-neutral-800 leading-[1.45]"
                >
                  {a}
                </li>
              ))}
              {education.thesisTitle && (
                <li className="text-[12px] text-neutral-800 leading-[1.45]">
                  Research: {education.thesisTitle}
                </li>
              )}
            </ul>
          </section>
        )}

        {/* Skills */}
        <section>
          <SectionHeading>Technical Skills</SectionHeading>
          <dl className="space-y-1 text-[12px] leading-[1.45]">
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2 sm:grid-cols-[8.5rem_1fr]">
              <dt className="font-semibold text-neutral-900">Mobile</dt>
              <dd className="text-neutral-800">
                React Native (JS/TS), Native Modules (Swift/Kotlin), Deep
                Linking, OTA Update (CodePush)
              </dd>
            </div>
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2 sm:grid-cols-[8.5rem_1fr]">
              <dt className="font-semibold text-neutral-900">Backend</dt>
              <dd className="text-neutral-800">
                {aboutData.backendSkills.join(", ")}
              </dd>
            </div>
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2 sm:grid-cols-[8.5rem_1fr]">
              <dt className="font-semibold text-neutral-900">Architecture</dt>
              <dd className="text-neutral-800">
                Modular design, scalable API, caching strategies, microservice
                communication
              </dd>
            </div>
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2 sm:grid-cols-[8.5rem_1fr]">
              <dt className="font-semibold text-neutral-900">CI/CD & DevOps</dt>
              <dd className="text-neutral-800">
                Docker, Nginx, AWS S3, Fastlane, GitHub Actions, VPS deployment
              </dd>
            </div>
            <div className="grid grid-cols-[7.5rem_1fr] gap-x-2 sm:grid-cols-[8.5rem_1fr]">
              <dt className="font-semibold text-neutral-900">Leadership</dt>
              <dd className="text-neutral-800">
                Task allocation, code review, project estimation,
                cross-functional collaboration
              </dd>
            </div>
          </dl>
        </section>

        {/* Additional */}
        <section className="pb-1">
          <SectionHeading>Additional Information</SectionHeading>
          <ul className="space-y-0.5 pl-4 list-disc marker:text-neutral-400 text-[12px] text-neutral-800 leading-[1.45]">
            <li>
              GitHub:{" "}
              <a
                href={heroData.socialLinks.github}
                className="underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-600"
                target="_blank"
                rel="noreferrer"
              >
                {heroData.socialLinks.github}
              </a>
            </li>
            <li>
              NestJS template:{" "}
              <a
                href="https://www.npmjs.com/package/nestjs-boilerplate-cli"
                className="underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-600"
                target="_blank"
                rel="noreferrer"
              >
                npmjs.com/package/nestjs-boilerplate-cli
              </a>
            </li>
            <li>
              Download PDF:{" "}
              <a
                href={heroData.downloadCvUrl}
                className="underline decoration-neutral-300 underline-offset-2 hover:decoration-neutral-600"
                target="_blank"
                rel="noreferrer"
              >
                TranDucGiang_SeniorSoftwareEngineer.pdf
              </a>
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};
